import Head from 'next/head';
import type { ReactElement } from 'react';

import { ourClientsCompaniesList } from '@/assets/clients/our-clients-companies/ourClientsCompaniesList';
import { caseStudies } from '@/components/Insights/caseStudies';
import CaseStudyArticle from '@/components/Insights/CaseStudyArticle';
import Layout from '@/components/Landing/Layout';
import Text from '@/components/Text';
import { createMetaTags } from '@/utils/seo';

const pegasusCaseStudy = caseStudies.find(({ slug }) => slug === 'pegasus')!;

const PegasusCaseStudy = () => {
  return (
    <>
      <Head>
        {createMetaTags({
          title:
            'Pegasus Solar Case Study - A Design Tool Rebuilt to Sell Hardware | MindPal',
          description:
            "How MindPal became Pegasus Solar's long-term product team, rebuilding the design tool contractors use to specify solar mounting hardware.",
        })}
      </Head>
      <CaseStudyArticle
        companyName={pegasusCaseStudy.companyName}
        logo={ourClientsCompaniesList.pegasus}
        tagline={pegasusCaseStudy.tagline}
        meta={[
          { label: 'Region', value: 'United States & Canada' },
          { label: 'Industry', value: pegasusCaseStudy.industry },
          {
            label: 'Engagement',
            value: 'Ongoing product partnership since 2023',
          },
        ]}
        heroImage={{
          src: '/case-studies/pegasus/hero-glide-dark.png',
          alt: 'Glide – Commercial solar platform built by MindPal for Pegasus Solar',
          width: 1440,
          height: 900,
        }}
        intro={
          <>
            Pegasus Solar builds mounting hardware that cuts how long a solar
            install takes. MindPal builds and runs the software that makes that
            advantage visible to every contractor deciding whose hardware to
            use. We`ve been their product team since 2023.
          </>
        }
        sections={[
          {
            eyebrow: 'Who they are',
            heading: "Hardware built to make the installer's day easier",
            image: {
              src: '/case-studies/pegasus/glide-workflow.png',
              alt: 'Glide design tool workflow builder for solar layout planning',
              width: 1200,
              height: 800,
            },
            body: (
              <Text className="text-jb-neutral-400">
                Pegasus Solar makes rooftop mounting and racking hardware —
                rails, mounts, clamps, and sealing systems — that`s gone into
                more than{' '}
                <strong className="text-jb-neutral-100">
                  150,000 installations
                </strong>{' '}
                across the US and Canada. Their competitive edge is labor
                efficiency: fewer parts, fewer attachment points, faster
                installs. They give contractors digital design tools to plan a
                layout before anyone gets on a roof — and that`s where the
                buying decision actually happens.
              </Text>
            ),
          },
          {
            eyebrow: 'The problem',
            heading: 'The design tool was undermining the hardware',
            body: (
              <>
                <Text className="text-jb-neutral-400">
                  For a company whose whole pitch is labor hours saved, the
                  design tool isn`t a nice-to-have — it`s where contractors
                  decide whose hardware to specify. Pegasus Solar`s tool had
                  been built by an outside vendor and wasn`t cutting it. Strong
                  engineering was being undermined by a weak interface.
                </Text>
                <Text className="text-jb-neutral-400">
                  Completing a layout also meant pulling structured data from
                  documents that never look the same twice: utility paperwork,
                  equipment specs, usage data — each with different field names,
                  units, and layouts. Standard parsing couldn`t handle the
                  variation, so the work stayed manual.
                </Text>
              </>
            ),
          },
          {
            eyebrow: 'What we built',
            heading: 'A product partnership, not a delivery date',
            image: {
              src: '/case-studies/pegasus/pegasus-analytics.png',
              alt: 'Pegasus Solar analytics dashboard showing project data and forecasting',
              width: 1200,
              height: 800,
            },
            imagePosition: 'right',
            body: (
              <>
                <Text className="text-jb-neutral-400">
                  MindPal took over the platform in 2023 and has owned it since
                  — ongoing development, a live feedback loop with contractors
                  using it, and a roadmap that moves as the business does:
                </Text>
                <ul className="flex list-disc flex-col gap-3 pl-5">
                  <li>
                    <Text className="text-jb-neutral-400">
                      <strong className="text-jb-neutral-100">
                        One continuous workflow.
                      </strong>{' '}
                      From first rough layout to a full implementation package,
                      with no handoffs or re-entry that used to break the
                      process.
                    </Text>
                  </li>
                  <li>
                    <Text className="text-jb-neutral-400">
                      <strong className="text-jb-neutral-100">
                        The hardware advantage, made visible.
                      </strong>{' '}
                      The tool shows exactly what SkipRail saves on a specific
                      roof — fewer rails, fewer attachment points, fewer labor
                      hours — right when the contractor is deciding what to
                      specify.
                    </Text>
                  </li>
                  <li>
                    <Text className="text-jb-neutral-400">
                      <strong className="text-jb-neutral-100">
                        AI where it actually solves the problem.
                      </strong>{' '}
                      Document parsing that handles any format variation, and
                      computer vision that outlines rooftops and flags
                      obstructions automatically.
                    </Text>
                  </li>
                </ul>
              </>
            ),
          },
          {
            eyebrow: 'What changed',
            heading: 'Savings shown at the moment of decision',
            image: {
              src: '/case-studies/pegasus/savings-comparison.jpg',
              alt: 'Pegasus Solar Glide comparison tool showing average cost savings per year',
              width: 1440,
              height: 850,
            },
            body: (
              <ul className="flex list-disc flex-col gap-3 pl-5">
                <li>
                  <Text className="text-jb-neutral-400">
                    A faster, more stable tool with fewer errors, and an
                    interface that finally matches the quality of the hardware.
                  </Text>
                </li>
                <li>
                  <Text className="text-jb-neutral-400">
                    More contractors who start a layout actually finish it and
                    go on to specify Pegasus hardware.
                  </Text>
                </li>
                <li>
                  <Text className="text-jb-neutral-400">
                    SkipRail`s labor savings shown and quantified at the moment
                    of decision, not just claimed in a brochure.
                  </Text>
                </li>
                <li>
                  <Text className="text-jb-neutral-400">
                    Contractors switching to Pegasus Solar from competing
                    suppliers because of the specification experience itself.
                  </Text>
                </li>
              </ul>
            ),
          },
        ]}
        closingHeading="Ready to build something like this?"
        closing={
          <Text className="text-center text-jb-neutral-400">
            We work as a long-term product team — designing, building, and
            shipping continuously. Let`s talk about what we could build
            together.
          </Text>
        }
      />
    </>
  );
};

export default PegasusCaseStudy;

PegasusCaseStudy.getLayout = (page: ReactElement) => (
  <Layout theme="dark">{page}</Layout>
);
